import PropTypes from 'prop-types';
import { Button, Col, Form, Row } from 'react-bootstrap';

const NewsEditorForm = ({
  formState,
  onChange,
  onSubmit,
  isSaving,
  onImageFileSelected,
  onUploadImage,
  isUploadingImage,
  selectedImageName,
  submitLabel = 'Save Draft',
}) => {
  return (
    <Form onSubmit={onSubmit}>
      <Row className="g-3">
        <Col md={8}>
          <Form.Group controlId="news-title">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={formState.title}
              onChange={(event) => onChange('title', event.target.value)}
              placeholder="Summer mileage challenge kickoff"
              maxLength={160}
              required
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="news-status">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={formState.status}
              onChange={(event) => onChange('status', event.target.value)}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={12}>
          <Form.Group controlId="news-summary">
            <Form.Label>Summary</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formState.summary}
              onChange={(event) => onChange('summary', event.target.value)}
              placeholder="Give members a quick reason to click into the story."
              maxLength={500}
              required
            />
          </Form.Group>
        </Col>
        <Col xs={12}>
          <Form.Group controlId="news-body">
            <Form.Label>Body</Form.Label>
            <Form.Control
              as="textarea"
              rows={12}
              value={formState.body}
              onChange={(event) => onChange('body', event.target.value)}
              placeholder="Tell the story, share dates, explain the event, and give members a clear next step."
              maxLength={20000}
              required
            />
          </Form.Group>
        </Col>
        <Col md={8}>
          <Form.Group controlId="news-image-url">
            <Form.Label>Image URL</Form.Label>
            <Form.Control
              type="url"
              value={formState.imageUrl}
              onChange={(event) => onChange('imageUrl', event.target.value)}
              placeholder="https://... or /images/news/story.jpg"
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="news-image-alt">
            <Form.Label>Image alt text</Form.Label>
            <Form.Control
              type="text"
              value={formState.imageAlt}
              onChange={(event) => onChange('imageAlt', event.target.value)}
              maxLength={180}
              placeholder="Describe the image"
            />
          </Form.Group>
        </Col>
        <Col md={8}>
          <Form.Group controlId="news-image-upload">
            <Form.Label>Upload image</Form.Label>
            <Form.Control
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={(event) => onImageFileSelected(event.target.files?.[0] || null)}
            />
            <Form.Text muted>
              JPEG, PNG, WEBP, or GIF up to 5 MB.
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={4} className="d-flex align-items-end">
          <Button type="button" variant="outline-primary" onClick={onUploadImage} disabled={isUploadingImage || !selectedImageName}>
            {isUploadingImage ? 'Uploading...' : selectedImageName ? `Upload ${selectedImageName}` : 'Choose a file'}
          </Button>
        </Col>
        {formState.imageUrl ? (
          <Col xs={12}>
            <div className="border rounded-4 overflow-hidden">
              <img
                src={formState.imageUrl}
                alt={formState.imageAlt || formState.title}
                style={{ width: '100%', maxHeight: 280, objectFit: 'cover' }}
              />
            </div>
          </Col>
        ) : null}
        <Col md={6}>
          <Form.Group controlId="news-tags">
            <Form.Label>Tags</Form.Label>
            <Form.Control
              type="text"
              value={formState.tags}
              onChange={(event) => onChange('tags', event.target.value)}
              placeholder="league event, announcement, community"
            />
          </Form.Group>
        </Col>
        <Col md={6} className="d-flex align-items-end">
          <Form.Check
            id="news-lock-comments"
            type="switch"
            label="Lock comments"
            checked={formState.lockedComments}
            onChange={(event) => onChange('lockedComments', event.target.checked)}
          />
        </Col>
      </Row>

      <div className="d-flex gap-2 mt-4 flex-wrap">
        <Button type="submit" disabled={isSaving}>
          {isSaving ? 'Saving...' : submitLabel}
        </Button>
      </div>
    </Form>
  );
};

NewsEditorForm.propTypes = {
  formState: PropTypes.shape({
    title: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    summary: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    imageUrl: PropTypes.string.isRequired,
    imageAlt: PropTypes.string.isRequired,
    tags: PropTypes.string.isRequired,
    lockedComments: PropTypes.bool.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
  onImageFileSelected: PropTypes.func.isRequired,
  onUploadImage: PropTypes.func.isRequired,
  isUploadingImage: PropTypes.bool.isRequired,
  selectedImageName: PropTypes.string,
  submitLabel: PropTypes.string,
};

export default NewsEditorForm;
